import { BffRequestHandler } from './helpers/BffRequestHandler';
import { isServerSideTrackingEnabledForOrganization } from './utils/index';

export interface AttributionEvent {
    event_type: string;
    event_time: string;
    event_id: string;
    stable_id: string;
    org_id: string;
    pixel_id: string;
    event_source_url?: string;
    event_data?: any;
    user_data?: UserData;
}

export interface UserData {
    email?: string;
    firstName?: string;
    lastName?: string;
    zipCode?: string;
    externalId?: string;
    phone?: string;
}

interface PixelConfig {
    pixelId: string;
    id: string;
    events: Record<string, string>; // Eventbrite event name -> Facebook event name mapping
}

export interface TrackingBeacons {
    'Facebook Pixel'?: PixelConfig[];
    [key: string]: PixelConfig[] | undefined; // Allow for any pixel type
}

interface FacebookPixelEvent {
    pixelId: string;
    facebookEventName: string;
}

// Create BFF request handler instance
const bffHandler = new BffRequestHandler('/conversions-manager');

// Consent check function for attribution calls
const checkAttributionConsent = (): boolean => {
    try {
        if (typeof window !== 'undefined' && window.airgap?.getConsent) {
            const consentData = window.airgap.getConsent();
            const advertisingConsent = consentData?.purposes?.Advertising;
            const saleOfInfoConsent = consentData?.purposes?.SaleOfInfo;

            // Check if we're in an opt-in jurisdiction
            const isOptInJurisdiction = (() => {
                if (window.airgap?.getRegimes) {
                    const regimes = Array.from(window.airgap.getRegimes());
                    const optInRegimes = ['GDPR', 'LGPD', 'PIPEDA'];
                    return optInRegimes.includes(regimes[0] || 'CPRA');
                }
                return false;
            })();

            if (advertisingConsent === true && saleOfInfoConsent === true) {
                return true;
            } else if (
                advertisingConsent === false ||
                saleOfInfoConsent === false
            ) {
                return false;
            } else {
                return !isOptInJurisdiction; // false for opt-in, true for opt-out
            }
        }
    } catch (error) {
        console.warn(
            '❌ [checkAttributionConsent] Failed to check consent:',
            error,
        );
    }

    // Fallback: allow attribution if consent check fails
    return true;
};

const uniqueFacebookEvents = (
    pixelEvents: FacebookPixelEvent[],
): FacebookPixelEvent[] => {
    const uniqueEvents = Array.from(
        new Map(
            pixelEvents.map((item) => [
                `${item.pixelId}-${item.facebookEventName}`,
                item,
            ]),
        ).values(),
    );

    return uniqueEvents;
};

const getFacebookEventNames = (
    eventbriteEventName: string,
    trackingConfig?: TrackingBeacons,
): FacebookPixelEvent[] => {
    try {
        if (!trackingConfig || !trackingConfig['Facebook Pixel']) {
            console.warn(
                '⚠️ [getFacebookEventNames] No Facebook Pixel data found',
            );
            return [];
        }

        const facebookPixelData = trackingConfig['Facebook Pixel'];

        // Filter to only Facebook pixels and check for event mappings
        const facebookPixelEvents: FacebookPixelEvent[] = [];

        facebookPixelData.forEach((pixel) => {
            // Always add the default FB pixel events
            if (eventbriteEventName === 'event_listing') {
                facebookPixelEvents.push({
                    pixelId: pixel.pixelId,
                    facebookEventName: 'PageView',
                });
            } else if (eventbriteEventName === 'event_order_confirmation') {
                facebookPixelEvents.push({
                    pixelId: pixel.pixelId,
                    facebookEventName: 'Purchase',
                });
            }

            if (pixel.events && pixel.events[eventbriteEventName]) {
                const facebookEventName = pixel.events[eventbriteEventName];

                facebookPixelEvents.push({
                    pixelId: pixel.pixelId,
                    facebookEventName: facebookEventName,
                });
            } else {
                console.log(
                    `⏭️ [getFacebookEventNames] No mapping found in pixel ${pixel.id} (${pixel.pixelId}) for ${eventbriteEventName}`,
                );
            }
        });

        const uniqueFacebookPixelEvents =
            uniqueFacebookEvents(facebookPixelEvents);

        return uniqueFacebookPixelEvents;
    } catch (error) {
        console.warn(
            '❌ [getFacebookEventNames] Error getting Facebook event names:',
            error,
        );
        return [];
    }
};

// Helper function to send event data to attribution service
export const sendEventToAttributionService = async (
    event: AttributionEvent,
): Promise<void> => {
    if (!checkAttributionConsent()) {
        console.log(
            '🚫 [sendEventToAttributionService] Consent not granted, skipping attribution call',
        );
        return;
    }

    try {
        await bffHandler.request('/api/v1/attribution/event', {
            method: 'POST',
            body: JSON.stringify(event),
        } as any);
    } catch (error) {
        console.warn('Attribution service error:', error);
        // Don't throw - attribution is non-blocking
    }
};

// Helper function to generate unique event ID for deduplication
export const generateEventId = (stableId: string): string => {
    return `${stableId}_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
};

// Helper function to safely access window.__SERVER_DATA__.user fields
const getServerUserField = (field: string): string | undefined => {
    try {
        return window?.__SERVER_DATA__?.user?.[field];
    } catch (error) {
        console.warn(
            `Failed to access window.__SERVER_DATA__.user.${field}:`,
            error,
        );
        return undefined;
    }
};

// Main function to send events with proper Facebook event names
export const sendEventToCourier = async (
    eventbriteEventName: string,
    eventIdForAttribution: string,
    stableId: string,
    organizationId: string,
    trackingBeacons: TrackingBeacons,
    eventData?: any,
    userData?: UserData,
): Promise<void> => {
    if (!isServerSideTrackingEnabledForOrganization(organizationId)) {
        return;
    }

    // Get the actual Facebook event names that will be fired
    const facebookEventsToFire = getFacebookEventNames(
        eventbriteEventName,
        trackingBeacons,
    );

    // Enhance userData with server data if needed (and available)
    let enhancedUserData: UserData | undefined = userData;
    if (
        !userData ||
        !userData.email ||
        !userData.firstName ||
        !userData.lastName ||
        !userData.externalId
    ) {
        const serverEmail = getServerUserField('email');
        const serverFirstName = getServerUserField('firstName');
        const serverLastName = getServerUserField('lastName');
        const serverPublicId = getServerUserField('publicId');

        enhancedUserData = {
            ...(userData?.email || serverEmail
                ? { email: userData?.email || serverEmail }
                : {}),
            ...(userData?.firstName || serverFirstName
                ? { firstName: userData?.firstName || serverFirstName }
                : {}),
            ...(userData?.lastName || serverLastName
                ? { lastName: userData?.lastName || serverLastName }
                : {}),
            ...(userData?.zipCode ? { zipCode: userData.zipCode } : {}),
            ...(userData?.phone ? { phone: userData.phone } : {}),
            ...(userData?.externalId || serverPublicId
                ? { externalId: userData?.externalId || serverPublicId }
                : {}),
        };
    }

    // Send each Facebook event to attribution service
    for (const facebookEventToFire of facebookEventsToFire) {
        const eventTime = `${Math.floor(Date.now() / 1000)}`;
        const eventSourceUrl = window?.location.href;

        sendEventToAttributionService({
            event_type: facebookEventToFire.facebookEventName, // Use actual Facebook event name
            event_time: eventTime,
            event_id: eventIdForAttribution,
            event_source_url: eventSourceUrl,
            stable_id: stableId,
            org_id: organizationId,
            pixel_id: facebookEventToFire.pixelId,
            event_data: eventData,
            user_data: enhancedUserData,
        });
    }
};
